import React, { useState } from 'react';
import { ShoppingCart, Menu, X, Heart, Search, User, MapPin, Phone, Mail, Instagram, Facebook, ChevronRight, Star, Check } from 'lucide-react';

// Mock data for products
const initialProducts = [
  {
    id: 1,
    name: "Asante Set",
    category: "African-styled Earrings",
    price: 45.99,
    image: "/images/asante-set.jpeg",
    images: [
      "/images/asante-set.jpeg"
    ],
    description: "Vibrant Afrocentric earrings celebrating traditional African beadwork. Handcrafted from upcycled materials, each piece honors cultural heritage while championing sustainability.",
    materials: "Upcycled beads, repurposed fabric, recycled metal",
    featured: true
  },
  {
    id: 2,
    name: "Jump Ring Elegance",
    category: "Drop Earrings",
    price: 42.99,
    image: "/images/jump-ring.jpeg",
    images: [
      "/images/jump-ring.jpeg",
      "/images/jump-ring2.jpeg"
    ],
    description: "Minimalist drop earrings crafted from repurposed jump rings and recycled metal. Simple elegance meets sustainable design.",
    materials: "Recycled jump rings, upcycled metal findings",
    featured: true
  },
  {
    id: 3,
    name: "Layered Tassel Beauty",
    category: "Tassel Earrings",
    price: 48.99,
    image: "/images/layerred-tassel.jpeg",
    images: [
      "/images/layerred-tassel.jpeg"
    ],
    description: "Bold layered tassel earrings made from upcycled fabric scraps and vintage textiles. Each layer tells a story of transformation from waste to wearable art.",
    materials: "Upcycled fabric, vintage textiles, recycled metal hooks",
    featured: true
  },
  {
    id: 4,
    name: "Contemporary Collection",
    category: "Drop Earrings",
    price: 38.99,
    image: "/images/product4.jpeg",
    images: [
      "/images/product4.jpeg",
      "/images/product5.jpeg"
    ],
    description: "Modern earrings crafted from repurposed materials with contemporary flair. Sustainable fashion for the conscious consumer.",
    materials: "Upcycled materials, recycled metal components",
    featured: false
  },
  {
    id: 5,
    name: "Raffia Earrings",
    category: "Tassel Earrings",
    price: 44.99,
    image: "/images/raffia-earring.jpeg",
    images: [
      "/images/raffia-earring.jpeg"
    ],
    description: "Natural raffia earrings honoring traditional Nigerian craftsmanship. Made from sustainable raffia fibers and upcycled metal findings.",
    materials: "Raffia fibers, upcycled metal, repurposed beads",
    featured: true
  },
  {
    id: 6,
    name: "Rhinestone Stud Collection",
    category: "Drop Earrings",
    price: 52.99,
    image: "/images/rhinestone-stud.jpeg",
    images: [
      "/images/rhinestone-stud.jpeg"
    ],
    description: "Elegant rhinestone studs crafted from repurposed broken jewelry and vintage crystals. We give discarded treasures a second life with sophisticated redesign.",
    materials: "Upcycled crystals, repurposed jewelry pieces, recycled findings",
    featured: false
  },
  {
    id: 7,
    name: "Tassel Rhinestone Earrings",
    category: "Tassel Earrings",
    price: 49.99,
    image: "/images/tassel-rhinestone.jpeg",
    images: [
      "/images/tassel-rhinestone.jpeg",
      "/images/tassel-rhinestone2.jpeg"
    ],
    description: "Stunning combination of upcycled fabric tassels and repurposed rhinestones. A perfect blend of texture, sparkle, and sustainability.",
    materials: "Upcycled fabric, vintage rhinestones, recycled metal",
    featured: true
  },
  {
    id: 8,
    name: "Statement Collection",
    category: "Hoop Earrings",
    price: 46.99,
    image: "/images/product7.jpeg",
    images: [
      "/images/product7.jpeg",
      "/images/product8.jpeg"
    ],
    description: "Bold statement pieces crafted from upcycled materials. Each earring makes a sustainable fashion statement while celebrating creativity.",
    materials: "Repurposed materials, recycled metal components",
    featured: false
  }
];

const testimonials = [
  {
    name: "Sarah M.",
    text: "Absolutely stunning pieces! The quality is exceptional and they arrived beautifully packaged.",
    rating: 5
  },
  {
    name: "Emma L.",
    text: "The jewelry-making class was incredible! I learned so much and made beautiful earrings.",
    rating: 5
  },
  {
    name: "Lisa K.",
    text: "My go-to place for unique, handmade jewelry. Every piece tells a story.",
    rating: 5
  }
];

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [priceRange, setPriceRange] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const categories = ['All', 'Tassel Earrings', 'Drop Earrings', 'Hoop Earrings', 'African-styled Earrings'];

  const filteredProducts = initialProducts.filter(product => {
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory;
    const searchMatch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    let priceMatch = true;

    if (priceRange === 'under40') priceMatch = product.price < 40;
    if (priceRange === '40to60') priceMatch = product.price >= 40 && product.price <= 60;
    if (priceRange === 'over60') priceMatch = product.price > 60;

    return categoryMatch && searchMatch && priceMatch;
  });

  const addToCart = (product) => {
    setCart([...cart, { ...product, cartId: Date.now() }]);
    alert('Added to cart!');
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const getTotalPrice = () => {
    return cart.reduce((sum, item) => sum + item.price, 0).toFixed(2);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty!');
      return;
    }
    alert('Redirecting to Stripe Checkout... (In production, this would open Stripe payment page)');
    // In production: window.location.href = '/api/create-checkout-session';
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for subscribing to our newsletter!');
    setNewsletterEmail('');
  };

  // Home Page
  const Header = () => (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <button onClick={() => setCurrentPage('home')} className="text-2xl font-serif text-rose-400">
              Mojis Mattel
            </button>
          </div>

          <nav className="hidden md:flex space-x-8">
            <button onClick={() => setCurrentPage('home')} className={`${currentPage === 'home' ? 'text-rose-400' : 'text-gray-700'} hover:text-rose-400 transition`}>Home</button>
            <button onClick={() => setCurrentPage('shop')} className={`${currentPage === 'shop' ? 'text-rose-400' : 'text-gray-700'} hover:text-rose-400 transition`}>Shop</button>
            <button onClick={() => setCurrentPage('training')} className={`${currentPage === 'training' ? 'text-rose-400' : 'text-gray-700'} hover:text-rose-400 transition`}>Training</button>
            <button onClick={() => setCurrentPage('contact')} className={`${currentPage === 'contact' ? 'text-rose-400' : 'text-gray-700'} hover:text-rose-400 transition`}>Contact</button>
          </nav>

          <div className="flex items-center space-x-4">
            <button className="text-gray-700 hover:text-rose-400 transition">
              <Search size={20} />
            </button>
            <button className="text-gray-700 hover:text-rose-400 transition">
              <Heart size={20} />
            </button>
            <button onClick={() => setShowCart(!showCart)} className="text-gray-700 hover:text-rose-400 transition relative">
              <ShoppingCart size={20} />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-rose-400 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {cart.length}
                </span>
              )}
            </button>
            <button onClick={() => setIsAdmin(!isAdmin)} className="text-gray-700 hover:text-rose-400 transition">
              <User size={20} />
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-700">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-2">
            <button onClick={() => { setCurrentPage('home'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-gray-700">Home</button>
            <button onClick={() => { setCurrentPage('shop'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-gray-700">Shop</button>
            <button onClick={() => { setCurrentPage('training'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-gray-700">Training</button>
            <button onClick={() => { setCurrentPage('contact'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-gray-700">Contact</button>
          </div>
        )}
      </div>

      {showCart && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white shadow-lg rounded-lg p-4 mr-4 max-h-96 overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Shopping Cart</h3>
          {cart.length === 0 ? (
            <p className="text-gray-500">Your cart is empty</p>
          ) : (
            <>
              {cart.map((item) => (
                <div key={item.cartId} className="flex justify-between items-center mb-3 pb-3 border-b">
                  <div className="flex items-center space-x-3">
                    <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                    <div>
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-sm text-rose-400">€{item.price}</p>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.cartId)} className="text-red-500 hover:text-red-700">
                    <X size={16} />
                  </button>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between mb-4">
                  <span className="font-semibold">Total:</span>
                  <span className="font-semibold text-rose-400">€{getTotalPrice()}</span>
                </div>
                <button onClick={handleCheckout} className="w-full bg-rose-400 text-white py-2 rounded-lg hover:bg-rose-500 transition">
                  Checkout
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );

  const HomePage = () => (
    <div>
      {/* Hero Section - Enhanced with Image & Animation */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
    {/* Background Image with Parallax Effect */}
    <div
      className="absolute inset-0 bg-cover bg-center transform scale-105 transition-transform duration-700 hover:scale-110"
      style={{
        backgroundImage: "url('/images/hero-image.png')",
        backgroundPosition: 'center center'
      }}
    ></div>

    {/* Gradient Overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>

    {/* Animated Pattern Overlay (optional) */}
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-0 left-0 w-full h-full bg-repeat"
        style={{
          backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.4\"%3E%3Cpath d=\"M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')",
          backgroundSize: '30px 30px'
        }}>
      </div>
    </div>

    {/* Content with Fade-in Animation */}
    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto animate-fade-in">
      {/* Small Badge */}
      <div className="inline-block bg-white/20 backdrop-blur-sm text-white px-6 py-2 rounded-full mb-6 text-sm font-semibold border border-white/30 animate-slide-down">
        🌍 100% Upcycled Materials • 🇳🇱 Made in Amsterdam
      </div>

      {/* Main Heading with Stagger Animation */}
      <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif text-white mb-6 drop-shadow-2xl animate-slide-up leading-tight">
        Waste to Wearable Art
      </h1>

      {/* Subheading */}
      <p className="text-xl md:text-2xl lg:text-3xl text-white/90 mb-4 drop-shadow-lg font-light animate-slide-up-delay">
        Sustainable Afrocentric jewelry handcrafted from upcycled materials
      </p>

      <p className="text-lg md:text-xl text-white/80 mb-10 drop-shadow-lg max-w-3xl mx-auto animate-slide-up-delay-2">
        Transforming soda cans, bottle caps & fabric scraps into vibrant earrings • Empowering youth through creative training
      </p>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up-delay-3">
        <button
          onClick={() => setCurrentPage('shop')}
          className="bg-rose-400 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-rose-500 transition-all transform hover:scale-105 shadow-2xl hover:shadow-rose-500/50"
        >
          Shop Collection
        </button>
        <button
          onClick={() => setCurrentPage('training')}
          className="bg-white/20 backdrop-blur-sm text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white/30 transition-all border-2 border-white/50 hover:border-white transform hover:scale-105"
        >
          Join Our Workshops
        </button>
      </div>

      {/* Trust Indicators */}
      <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/70 text-sm animate-fade-in-delay">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span>Registered Social Enterprise</span>
        </div>
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>KVK: 88321290</span>
        </div>
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>EU Circular Economy Aligned</span>
        </div>
      </div>
    </div>

    {/* Scroll Down Indicator */}
    <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
      <svg className="w-6 h-6 text-white opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </div>
  </section>

  {/* Featured Collections */ }
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
    <h2 className="text-4xl font-serif text-center mb-4">Featured Collections</h2>
    <p className="text-center text-gray-600 mb-12">Explore our most loved pieces</p>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {initialProducts.filter(p => p.featured).map((product) => (
        <div key={product.id} className="group cursor-pointer" onClick={() => { setSelectedProduct(product); setCurrentPage('product'); }}>
          <div className="relative overflow-hidden rounded-lg mb-4 aspect-square">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
            <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition"></div>
          </div>
          <h3 className="font-semibold text-gray-800">{product.name}</h3>
          <p className="text-sm text-gray-500">{product.category}</p>
          <p className="text-rose-400 font-semibold mt-2">€{product.price}</p>
        </div>
      ))}
    </div>
  </section>

  {/* About Section */ }
  <section className="bg-rose-50 py-20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-4xl font-serif mb-6">Transforming Waste into Wearable Art</h2>
          <p className="text-gray-700 mb-4">
            Mojis Mattel is a socially-driven jewelry brand that transforms discarded materials into vibrant, Afrocentric earrings. We use upcycled items such as soda cans, bottle caps, vintage buttons, old book pages, and fabric scraps to craft unique, handmade accessories.
          </p>
          <p className="text-gray-700 mb-4">
            Rooted in Nigerian heritage and Amsterdam's sustainability values, each piece tells a story of transformation—turning waste into beauty while promoting environmental consciousness and celebrating African culture.
          </p>
          <p className="text-gray-700 mb-6">
            <strong>More than jewelry:</strong> We empower young people, especially from underserved backgrounds, through workshops that teach jewelry-making skills, design thinking, and entrepreneurship—turning creativity into opportunity.
          </p>
          <button onClick={() => setCurrentPage('shop')} className="text-rose-400 font-semibold flex items-center hover:text-rose-500">
            Explore Our Collection <ChevronRight size={20} />
          </button>
        </div>
        <div className="rounded-lg overflow-hidden shadow-xl">
          <img src="https://images.unsplash.com/photo-1532630571098-79a3d222b00d?w=600&h=600&fit=crop" alt="Upcycled Materials" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>

  {/* Our Impact - Sustainability & Social Mission */ }
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
    <h2 className="text-4xl font-serif text-center mb-4">Our Impact</h2>
    <p className="text-center text-gray-600 mb-12">Creating positive change through sustainable fashion and community empowerment</p>

    <div className="grid md:grid-cols-3 gap-8">
      {/* Environmental Impact */}
      <div className="bg-green-50 p-8 rounded-lg shadow-md">
        <div className="w-16 h-16 bg-green-400 rounded-full flex items-center justify-center mb-4 mx-auto">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-center mb-3 text-gray-800">Circular Economy</h3>
        <p className="text-gray-700 text-center">
          100% upcycled materials including soda cans, bottle caps, vintage buttons, and fabric scraps. We divert waste from landfills and give discarded items new life as wearable art.
        </p>
      </div>

      {/* Social Impact */}
      <div className="bg-amber-50 p-8 rounded-lg shadow-md">
        <div className="w-16 h-16 bg-amber-400 rounded-full flex items-center justify-center mb-4 mx-auto">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-center mb-3 text-gray-800">Youth Empowerment</h3>
        <p className="text-gray-700 text-center">
          We train young people, especially African migrants, in jewelry-making, design, and entrepreneurship—turning creativity into opportunity and fostering economic independence.
        </p>
      </div>

      {/* Cultural Impact */}
      <div className="bg-rose-50 p-8 rounded-lg shadow-md">
        <div className="w-16 h-16 bg-rose-400 rounded-full flex items-center justify-center mb-4 mx-auto">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-center mb-3 text-gray-800">Cultural Heritage</h3>
        <p className="text-gray-700 text-center">
          Celebrating Afrocentric design traditions rooted in Nigerian craftsmanship, blending cultural storytelling with contemporary sustainable fashion.
        </p>
      </div>
    </div>

    {/* EU Alignment Badge */}
    <div className="mt-12 bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg border-l-4 border-green-500">
      <p className="text-center text-gray-700">
        <strong>🇪🇺 Aligned with EU Circular Economy Action Plan</strong><br />
        Supporting the Netherlands' goal of a fully circular economy by 2050
      </p>
    </div>
  </section>

  {/* Testimonials */ }
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
    <h2 className="text-4xl font-serif text-center mb-12">What Our Customers Say</h2>
    <div className="grid md:grid-cols-3 gap-8">
      {testimonials.map((testimonial, idx) => (
        <div key={idx} className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex mb-4">
            {[...Array(testimonial.rating)].map((_, i) => (
              <Star key={i} size={20} className="fill-amber-400 text-amber-400" />
            ))}
          </div>
          <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
          <p className="font-semibold text-gray-800">- {testimonial.name}</p>
        </div>
      ))}
    </div>
  </section>

  {/* Materials We Use - Upcycling Showcase */ }
  <section className="bg-white py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-4xl font-serif text-center mb-4">Materials We Transform</h2>
      <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
        Every piece of jewelry starts as discarded waste. We source locally, collect from households, and partner with community centers to give new life to forgotten materials.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
        {/* Material Card 1 */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">🥫</div>
          <h3 className="font-semibold text-gray-800 mb-2">Soda Cans</h3>
          <p className="text-sm text-gray-600">Aluminum cans transformed into lightweight statement hoops</p>
        </div>

        {/* Material Card 2 */}
        <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">🧵</div>
          <h3 className="font-semibold text-gray-800 mb-2">Fabric Scraps</h3>
          <p className="text-sm text-gray-600">Vintage textiles & clothing remnants become beautiful tassels</p>
        </div>

        {/* Material Card 3 */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">🔘</div>
          <h3 className="font-semibold text-gray-800 mb-2">Vintage Buttons</h3>
          <p className="text-sm text-gray-600">Old buttons & broken jewelry find new purpose</p>
        </div>

        {/* Material Card 4 */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">🍾</div>
          <h3 className="font-semibold text-gray-800 mb-2">Bottle Caps</h3>
          <p className="text-sm text-gray-600">Metal caps upcycled into vibrant African-inspired designs</p>
        </div>

        {/* Material Card 5 */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">📎</div>
          <h3 className="font-semibold text-gray-800 mb-2">Metal Findings</h3>
          <p className="text-sm text-gray-600">Paperclips, old keys & hardware repurposed as unique accents</p>
        </div>

        {/* Material Card 6 */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">💎</div>
          <h3 className="font-semibold text-gray-800 mb-2">Rhinestones</h3>
          <p className="text-sm text-gray-600">Salvaged crystals from broken jewelry given new sparkle</p>
        </div>

        {/* Material Card 7 */}
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">📄</div>
          <h3 className="font-semibold text-gray-800 mb-2">Book Pages</h3>
          <p className="text-sm text-gray-600">Old books & magazines transformed into artistic elements</p>
        </div>

        {/* Material Card 8 */}
        <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-lg shadow-md text-center hover:shadow-xl transition">
          <div className="text-4xl mb-3">🌾</div>
          <h3 className="font-semibold text-gray-800 mb-2">Raffia Fibers</h3>
          <p className="text-sm text-gray-600">Natural raffia honoring traditional Nigerian craftsmanship</p>
        </div>
      </div>

      {/* Sourcing Info */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 p-8 rounded-lg border-l-4 border-green-500">
        <h3 className="font-semibold text-lg mb-3 text-center">♻️ How We Source Materials</h3>
        <p className="text-gray-700 text-center max-w-4xl mx-auto">
          We partner with local residents, community centers, and organizations across Amsterdam to collect household waste materials. Through monthly "Material Upcycle Days," we invite people to donate items and learn about sustainable design. Every material is logged, sorted, and transformed with care—turning your discards into our design inspiration.
        </p>
      </div>
    </div>
  </section>

  {/* Newsletter */ }
  <section className="bg-gradient-to-r from-rose-400 to-amber-400 py-16">
    <div className="max-w-4xl mx-auto px-4 text-center">
      <h2 className="text-3xl font-serif text-white mb-4">Join Our Newsletter</h2>
      <p className="text-white mb-8">Get exclusive updates on new collections and special offers</p>
      <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 justify-center">
        <input
          type="email"
          value={newsletterEmail}
          onChange={(e) => setNewsletterEmail(e.target.value)}
          placeholder="Enter your email"
          className="px-6 py-3 rounded-full w-full sm:w-96 focus:outline-none focus:ring-2 focus:ring-white"
          required
        />
        <button type="submit" className="bg-white text-rose-400 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition">
          Subscribe
        </button>
      </form>
    </div>
  </section>
    </div >
  );

  // Shop Page
  const ShopPage = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-4xl font-serif mb-8">Shop Our Collection</h1>

      {/* Filters */}
      <div className="mb-8 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
          <select
            value={priceRange}
            onChange={(e) => setPriceRange(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
          >
            <option value="all">All Prices</option>
            <option value="under40">Under €40</option>
            <option value="40to60">€40 - €60</option>
            <option value="over60">Over €60</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search products..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProducts.map((product) => (
          <div key={product.id} className="group cursor-pointer bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition">
            <div className="relative aspect-square overflow-hidden" onClick={() => { setSelectedProduct(product); setCurrentPage('product'); }}>
              <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-lg text-gray-800 mb-1">{product.name}</h3>
              <p className="text-sm text-gray-500 mb-3">{product.category}</p>
              <div className="flex justify-between items-center">
                <p className="text-rose-400 font-bold text-xl">€{product.price}</p>
                <button
                  onClick={() => addToCart(product)}
                  className="bg-rose-400 text-white px-4 py-2 rounded-lg hover:bg-rose-500 transition"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No products found matching your criteria.</p>
        </div>
      )}
    </div>
  );

  // Product Detail Page
  const ProductDetailPage = () => {
    if (!selectedProduct) return null;

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button onClick={() => setCurrentPage('shop')} className="text-rose-400 flex items-center mb-6 hover:text-rose-500">
          <ChevronRight size={20} className="rotate-180" /> Back to Shop
        </button>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="rounded-lg overflow-hidden mb-4">
              <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {selectedProduct.images.map((img, idx) => (
                <img key={idx} src={img} alt={`${selectedProduct.name} ${idx + 1}`} className="rounded-lg cursor-pointer hover:opacity-75 transition" />
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500 mb-2">{selectedProduct.category}</p>
            <h1 className="text-4xl font-serif mb-4">{selectedProduct.name}</h1>
            <p className="text-3xl text-rose-400 font-bold mb-6">€{selectedProduct.price}</p>
            <p className="text-gray-700 mb-8">{selectedProduct.description}</p>

            <div className="mb-8">
              <h3 className="font-semibold mb-3">Features:</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-700">
                  <Check size={20} className="text-rose-400 mr-2" />
                  Handcrafted with premium materials
                </li>
                <li className="flex items-center text-gray-700">
                  <Check size={20} className="text-rose-400 mr-2" />
                  Hypoallergenic and nickel-free
                </li>
                <li className="flex items-center text-gray-700">
                  <Check size={20} className="text-rose-400 mr-2" />
                  Lightweight and comfortable
                </li>
                <li className="flex items-center text-gray-700">
                  <Check size={20} className="text-rose-400 mr-2" />
                  Comes in beautiful gift packaging
                </li>
              </ul>
            </div>

            <button
              onClick={() => addToCart(selectedProduct)}
              className="w-full bg-rose-400 text-white py-4 rounded-lg font-semibold text-lg hover:bg-rose-500 transition transform hover:scale-105"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Training Page
  const TrainingPage = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-serif mb-4">Youth Empowerment Through Jewelry Making</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          More than training—we empower young people, especially African migrants, to transform creativity into opportunity
        </p>
      </div>

      {/* Mission Statement */}
      <div className="bg-gradient-to-r from-rose-50 to-amber-50 p-8 rounded-lg shadow-md mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-center">Our Social Mission</h2>
        <p className="text-gray-700 text-center max-w-4xl mx-auto mb-4">
          Mojis Mattel teaches young people, especially those from underserved African migrant backgrounds, how to create beautiful jewelry from upcycled materials. Through hands-on workshops, we equip youth with practical skills in design, craftsmanship, and entrepreneurship—turning waste into wearable art and creativity into economic opportunity.
        </p>
        <p className="text-gray-700 text-center max-w-4xl mx-auto">
          <strong>Who we serve:</strong> Young people aged 15-35, with a focus on African migrants in the Netherlands seeking creative skills, economic independence, and community connection.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-12 mb-12">
        <div>
          <img src="/images/workshop.png" alt="Jewelry Making Workshop" className="rounded-lg shadow-lg w-full" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold mb-4">What You'll Learn</h2>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start">
              <Check size={20} className="text-rose-400 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Upcycling Skills:</strong> Transform household waste (soda cans, bottle caps, fabric scraps, vintage buttons) into stunning jewelry</span>
            </li>
            <li className="flex items-start">
              <Check size={20} className="text-rose-400 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Design & Craftsmanship:</strong> Learn jewelry design principles, color theory, and hands-on technical skills</span>
            </li>
            <li className="flex items-start">
              <Check size={20} className="text-rose-400 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Entrepreneurship:</strong> Basic business skills, pricing strategies, and how to sell your creations</span>
            </li>
            <li className="flex items-start">
              <Check size={20} className="text-rose-400 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Sustainability:</strong> Understanding circular economy and eco-conscious production</span>
            </li>
            <li className="flex items-start">
              <Check size={20} className="text-rose-400 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Take Home Your Creations:</strong> Create unique earrings to keep or sell during each workshop</span>
            </li>
          </ul>
          <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
            <p className="font-semibold mb-2">🌍 Community Impact</p>
            <p className="text-gray-700 text-sm">
              Our training program supports migrant integration, fosters economic independence, and builds creative communities across Amsterdam.
            </p>
          </div>
        </div>
      </div>

      {/* Workshop Details */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="font-semibold text-lg mb-3 text-rose-400">Workshop Duration</h3>
          <p className="text-gray-700">3-hour hands-on sessions</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="font-semibold text-lg mb-3 text-rose-400">Group Size</h3>
          <p className="text-gray-700">Small groups (max 8 participants) for personalized attention</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="font-semibold text-lg mb-3 text-rose-400">Investment</h3>
          <p className="text-gray-700">€85 per person - all materials included</p>
          <p className="text-sm text-gray-500 mt-2">Subsidized rates available for youth programs</p>
        </div>
      </div>

      {/* Booking Section */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-semibold mb-6 text-center">Book Your Workshop</h2>
        <p className="text-center text-gray-600 mb-6">
          Join our next workshop or inquire about group bookings for community organizations, schools, or youth programs
        </p>
        <div className="aspect-video bg-gradient-to-br from-rose-50 to-amber-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-700 mb-4 font-semibold">Ready to Transform Waste into Wearable Art?</p>
            <p className="text-sm text-gray-600 mb-6">
              Individual bookings • Group workshops • School programs • Community partnerships
            </p>
            <div className="space-y-3">
              <a
                href="mailto:info@mojismattel.com?subject=Workshop Booking Inquiry"
                className="inline-block bg-rose-400 text-white px-8 py-3 rounded-lg hover:bg-rose-500 transition font-semibold"
              >
                Book Workshop via Email
              </a>
              <p className="text-sm text-gray-500">
                Or call: <a href="tel:+31616385457" className="text-rose-400 hover:underline">+31 616 385 457</a>
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-4 italic">
              Calendly integration coming soon for direct online booking
            </p>
          </div>
        </div>
      </div>

      {/* Impact Statement */}
      <div className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-lg text-center">
        <h3 className="text-xl font-semibold mb-3">Creating Opportunity, One Earring at a Time</h3>
        <p className="text-gray-700 max-w-3xl mx-auto">
          By combining sustainability education with practical skills training, Mojis Mattel contributes to the Netherlands' goals for circular economy, migrant integration, and inclusive entrepreneurship.
        </p>
      </div>
    </div>
  );

  // Contact Page
  const ContactPage = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-serif mb-4">Get In Touch</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Whether you're interested in our jewelry, want to book a workshop, or explore partnership opportunities—we'd love to hear from you.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">Contact Information</h2>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <MapPin size={24} className="text-rose-400 mr-4 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Business Address</p>
                <p className="text-gray-600">
                  Pietersbergweg 932<br />
                  1105BM Amsterdam<br />
                  Netherlands
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Phone size={24} className="text-rose-400 mr-4 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:+31616385457" className="text-gray-600 hover:text-rose-400 transition">
                  +31 616 385 457
                </a>
                <p className="text-sm text-gray-500 mt-1">Available Mon-Sat, 9:00 AM - 6:00 PM</p>
              </div>
            </div>

            <div className="flex items-start">
              <Mail size={24} className="text-rose-400 mr-4 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:info@mojismattel.com" className="text-gray-600 hover:text-rose-400 transition">
                  info@mojismattel.com
                </a>
                <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
              </div>
            </div>
          </div>

          {/* Business Registration */}
          <div className="bg-blue-50 p-6 rounded-lg mb-8 border-l-4 border-blue-500">
            <h3 className="font-semibold mb-3">Registered Business</h3>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Legal Name:</strong> Mojis Mattel
            </p>
            <p className="text-sm text-gray-700 mb-2">
              <strong>KVK Number:</strong> 88321290
            </p>
            <p className="text-sm text-gray-700 mb-2">
              <strong>SBI Code:</strong> 47716 (Retail sale of jewelry)
            </p>
            <p className="text-sm text-gray-700">
              <strong>Structure:</strong> Sole Proprietorship
            </p>
          </div>

          <div className="mb-8">
            <h3 className="font-semibold mb-4">Connect With Us</h3>
            <div className="flex space-x-4">
              <a href="https://instagram.com/mojismattel" target="_blank" rel="noopener noreferrer" className="bg-rose-400 text-white p-3 rounded-full hover:bg-rose-500 transition">
                <Instagram size={24} />
              </a>
              <a href="https://facebook.com/mojismattel" target="_blank" rel="noopener noreferrer" className="bg-rose-400 text-white p-3 rounded-full hover:bg-rose-500 transition">
                <Facebook size={24} />
              </a>
            </div>
          </div>

          {/* Google Maps Embed */}
          <div className="rounded-lg overflow-hidden shadow-lg h-64">
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <div className="text-center text-gray-600">
                <MapPin size={48} className="mx-auto mb-2 text-rose-400" />
                <p className="font-semibold">Visit Us in Amsterdam</p>
                <p className="text-sm">Pietersbergweg 932, 1105BM</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-semibold mb-6">Send Us a Message</h2>

          {/* Message Type Selection */}
          <div className="bg-gradient-to-r from-rose-50 to-amber-50 p-4 rounded-lg mb-6">
            <p className="text-sm font-semibold mb-2">What can we help you with?</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center">
                <Check size={16} className="text-rose-400 mr-2" />
                <span>Product inquiries</span>
              </div>
              <div className="flex items-center">
                <Check size={16} className="text-rose-400 mr-2" />
                <span>Workshop bookings</span>
              </div>
              <div className="flex items-center">
                <Check size={16} className="text-rose-400 mr-2" />
                <span>Wholesale partnerships</span>
              </div>
              <div className="flex items-center">
                <Check size={16} className="text-rose-400 mr-2" />
                <span>Custom orders</span>
              </div>
              <div className="flex items-center">
                <Check size={16} className="text-rose-400 mr-2" />
                <span>Community programs</span>
              </div>
              <div className="flex items-center">
                <Check size={16} className="text-rose-400 mr-2" />
                <span>Material donations</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleContactSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows="6"
                placeholder="Tell us about your inquiry, workshop interest, or partnership opportunity..."
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                required
              ></textarea>
            </div>

            <button type="submit" className="w-full bg-rose-400 text-white py-3 rounded-lg font-semibold hover:bg-rose-500 transition">
              Send Message
            </button>
          </form>

          <p className="text-sm text-gray-500 text-center mt-6">
            We typically respond within 24 hours. For urgent workshop bookings, please call us directly.
          </p>
        </div>
      </div>
    </div>
  );

  // Admin Dashboard
  const AdminDashboard = () => {
    const [newProduct, setNewProduct] = useState({
      name: '',
      category: 'Tassel Earrings',
      price: '',
      description: '',
      image: ''
    });

    const handleAddProduct = (e) => {
      e.preventDefault();
      alert('Product added successfully! (In production, this would save to MongoDB)');
      setNewProduct({ name: '', category: 'Tassel Earrings', price: '', description: '', image: '' });
    };

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-serif">Admin Dashboard</h1>
          <button onClick={() => setIsAdmin(false)} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700">
            Exit Admin
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Add Product Form */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-6">Add New Product</h2>
            <form onSubmit={handleAddProduct} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                >
                  <option>Tassel Earrings</option>
                  <option>Drop Earrings</option>
                  <option>Hoop Earrings</option>
                  <option>African-styled Earrings</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                  required
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                <input
                  type="url"
                  value={newProduct.image}
                  onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-rose-400 focus:outline-none"
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> In production, this would support multiple image uploads to Cloudinary/Firebase Storage with drag-and-drop functionality.
                </p>
              </div>

              <button type="submit" className="w-full bg-rose-400 text-white py-3 rounded-lg font-semibold hover:bg-rose-500 transition">
                Add Product
              </button>
            </form>
          </div>

          {/* Product Management */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-6">Manage Products</h2>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {initialProducts.map((product) => (
                <div key={product.id} className="flex items-center justify-between border-b pb-4">
                  <div className="flex items-center space-x-4">
                    <img src={product.image} alt={product.name} className="w-16 h-16 object-cover rounded" />
                    <div>
                      <p className="font-semibold">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.category}</p>
                      <p className="text-rose-400 font-semibold">€{product.price}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm">
                      Edit
                    </button>
                    <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Order Management */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">Recent Orders</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Order ID</th>
                  <th className="text-left py-3 px-4">Customer</th>
                  <th className="text-left py-3 px-4">Product</th>
                  <th className="text-left py-3 px-4">Amount</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-4">#ORD001</td>
                  <td className="py-3 px-4">Jane Doe</td>
                  <td className="py-3 px-4">Rose Gold Tassel Elegance</td>
                  <td className="py-3 px-4">€45.99</td>
                  <td className="py-3 px-4">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">Completed</span>
                  </td>
                  <td className="py-3 px-4">
                    <button className="text-blue-500 hover:text-blue-700">View</button>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4">#ORD002</td>
                  <td className="py-3 px-4">John Smith</td>
                  <td className="py-3 px-4">Pearl Drop Classics</td>
                  <td className="py-3 px-4">€52.99</td>
                  <td className="py-3 px-4">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm">Processing</span>
                  </td>
                  <td className="py-3 px-4">
                    <button className="text-blue-500 hover:text-blue-700">View</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Footer
  const Footer = () => (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-2xl font-serif mb-4">Mojis Mattel</h3>
            <p className="text-gray-400 mb-4">Transforming waste into wearable art since 2022</p>
            <p className="text-sm text-gray-500">
              <strong>KVK:</strong> 88321290<br />
              <strong>SBI:</strong> 47716
            </p>
            <div className="mt-4">
              <span className="inline-block bg-green-600 text-white text-xs px-3 py-1 rounded-full">
                🌍 100% Upcycled Materials
              </span>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><button onClick={() => setCurrentPage('home')} className="text-gray-400 hover:text-white transition">Home</button></li>
              <li><button onClick={() => setCurrentPage('shop')} className="text-gray-400 hover:text-white transition">Shop</button></li>
              <li><button onClick={() => setCurrentPage('training')} className="text-gray-400 hover:text-white transition">Youth Training</button></li>
              <li><button onClick={() => setCurrentPage('contact')} className="text-gray-400 hover:text-white transition">Contact</button></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Our Mission</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>✓ Sustainable Fashion</li>
              <li>✓ Youth Empowerment</li>
              <li>✓ Circular Economy</li>
              <li>✓ Cultural Heritage</li>
              <li>✓ Community Impact</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>Pietersbergweg 932</li>
              <li>1105BM Amsterdam</li>
              <li>Netherlands</li>
              <li className="pt-2">
                <a href="tel:+31616385457" className="hover:text-white transition">
                  📞 +31 616 385 457
                </a>
              </li>
              <li>
                <a href="mailto:info@mojismattel.com" className="hover:text-white transition">
                  ✉️ info@mojismattel.com
                </a>
              </li>
            </ul>
            <div className="mt-4 flex space-x-3">
              <a href="https://instagram.com/mojismattel" target="_blank" rel="noopener noreferrer" className="bg-rose-400 text-white p-2 rounded-full hover:bg-rose-500 transition">
                <Instagram size={20} />
              </a>
              <a href="https://facebook.com/mojismattel" target="_blank" rel="noopener noreferrer" className="bg-rose-400 text-white p-2 rounded-full hover:bg-rose-500 transition">
                <Facebook size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
            <div>
              <p>&copy; 2026 Mojis Mattel. All rights reserved.</p>
              <p className="mt-1">Registered Sole Proprietorship | KVK: 88321290</p>
            </div>
            <div className="md:text-right">
              <p className="text-green-400">🇪🇺 Supporting EU Circular Economy Goals</p>
              <p className="mt-1">Aligned with Netherlands 2050 Sustainability Vision</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {isAdmin ? (
          <AdminDashboard />
        ) : (
          <>
            {currentPage === 'home' && <HomePage />}
            {currentPage === 'shop' && <ShopPage />}
            {currentPage === 'product' && <ProductDetailPage />}
            {currentPage === 'training' && <TrainingPage />}
            {currentPage === 'contact' && <ContactPage />}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default App;